import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO, isWeekend } from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Clock, User, Phone, MapPin, Calendar, Loader2, CalendarPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;
const HOURS = Array.from({ length: 12 }, (_, i) => i + 9); // 9h-20h
const currentUser = () => localStorage.getItem("label_energie_user") || "";

export default function PlanningRegie() {
  const navigate = useNavigate();
  const [rdvs, setRdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [showNewRdv, setShowNewRdv] = useState(false);
  const [newRdvForm, setNewRdvForm] = useState({ client_nom: "", client_telephone: "", notes: "", date: "", heure: "", dossier_id: "", dossierSearch: "" });
  const [savingRdv, setSavingRdv] = useState(false);
  const [busySlotsNew, setBusySlotsNew] = useState([]);
  const [dossiersList, setDossiersList] = useState([]);
  const [assignedAdmins, setAssignedAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);

  // Mon-Fri only (5 days)
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(currentWeekStart, i));

  // Fetch assigned admin(s) for the current user
  useEffect(() => {
    const fetchAdmins = async () => {
      setLoadingAdmins(true);
      try {
        const res = await fetch(`${API}/users/assigned-admin/${encodeURIComponent(currentUser())}`);
        const data = await res.json();
        setAssignedAdmins(data.admins || []);
      } catch (err) {
        setAssignedAdmins([]);
      }
      setLoadingAdmins(false);
    };
    fetchAdmins();
  }, []);

  const fetchRdvs = useCallback(async () => {
    if (assignedAdmins.length === 0 && !loadingAdmins) { setLoading(false); return; }
    setLoading(true);
    try {
      const weekEnd = addDays(currentWeekStart, 4); // Friday
      const params = new URLSearchParams({
        date_debut: format(currentWeekStart, "yyyy-MM-dd"),
        date_fin: format(weekEnd, "yyyy-MM-dd"),
        created_by: currentUser(),
      });
      // Add assigned admin to query
      if (assignedAdmins.length > 0) {
        params.set("personne", assignedAdmins[0].username);
      }
      const res = await fetch(`${API}/planning/rdvs?${params}`);
      const data = await res.json();
      setRdvs(data.rdvs || []);
    } catch (err) {
      setRdvs([]);
    }
    setLoading(false);
  }, [currentWeekStart, assignedAdmins, loadingAdmins]);

  useEffect(() => { if (!loadingAdmins) fetchRdvs(); }, [fetchRdvs, loadingAdmins]);

  // Fetch dossiers for the current user
  useEffect(() => {
    fetch(`${API}/dossiers`).then(r => r.json()).then(data => {
      const user = currentUser();
      setDossiersList(data.filter(d => d.assigned_to === user || d.created_by === user));
    }).catch(() => {});
  }, []);

  const adminName = assignedAdmins.length > 0 ? assignedAdmins[0].username : "Admin";

  // Fetch busy slots when date changes
  useEffect(() => {
    if (!newRdvForm.date || !adminName) return;
    fetch(`${API}/planning/busy-slots/${encodeURIComponent(adminName)}?date=${newRdvForm.date}`)
      .then(r => r.json()).then(d => setBusySlotsNew(d.busy_slots || [])).catch(() => setBusySlotsNew([]));
  }, [newRdvForm.date, adminName]);

  const handleCreateRdv = async () => {
    if (!newRdvForm.client_nom.trim() || !newRdvForm.date || !newRdvForm.heure) return;
    setSavingRdv(true);
    try {
      await fetch(`${API}/planning/rdvs`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: newRdvForm.date, heure: newRdvForm.heure,
          attribue_a: adminName,
          client_nom: newRdvForm.client_nom, client_telephone: newRdvForm.client_telephone,
          notes: newRdvForm.notes, programme: "",
          created_by: currentUser(), rdv_type: "rdv_admin",
          dossier_id: newRdvForm.dossier_id || null,
        }),
      });
      toast.success("RDV créé");
      setShowNewRdv(false);
      setNewRdvForm({ client_nom: "", client_telephone: "", notes: "", date: "", heure: "", dossier_id: "", dossierSearch: "" });
      fetchRdvs();
    } catch (err) { toast.error("Erreur"); }
    setSavingRdv(false);
  };

  const getRdvsForSlot = (day, hour) =>
    rdvs.filter(r => isSameDay(parseISO(r.date), day) && parseInt(r.heure?.split(":")[0]) === hour);

  const isMyRdv = (rdv) => rdv.created_by === currentUser();

  const isSlotBusyByAdmin = (day, hour) => {
    const slotRdvs = getRdvsForSlot(day, hour);
    return slotRdvs.some(r => !isMyRdv(r));
  };

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="planning-regie-page">
      <div>
        <h1 className="font-heading text-2xl font-bold">Planning — {adminName}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Créneaux de 1h, Lun-Ven 9h-20h. Les créneaux grisés sont occupés par {adminName}.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))} className="rounded-xl" data-testid="btn-prev-week"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="rounded-xl">Aujourd'hui</Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))} className="rounded-xl" data-testid="btn-next-week"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => setShowNewRdv(true)} className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white" data-testid="btn-create-rdv-regie">
            <CalendarPlus className="w-4 h-4 mr-2" />Créer un RDV
          </Button>
          <h2 className="font-semibold text-sm">
            {format(currentWeekStart, "d MMMM", { locale: fr })} — {format(addDays(currentWeekStart, 4), "d MMMM yyyy", { locale: fr })}
          </h2>
        </div>
      </div>

      {loadingAdmins ? (
        <div className="text-center py-12 text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Chargement du planning...
        </div>
      ) : assignedAdmins.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-xl">
          <User className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">Aucun admin attitré</p>
          <p className="text-xs text-muted-foreground mt-1">Contactez Renova pour qu'un admin vous soit attribué dans les paramètres</p>
        </div>
      ) : (
        <div className="border border-border rounded-xl bg-card overflow-hidden">
          <div className="grid grid-cols-6 border-b border-border">
            <div className="p-3 bg-muted/50 border-r border-border"><span className="text-xs font-medium text-muted-foreground">Heure</span></div>
            {weekDays.map((day, i) => {
              const isToday = isSameDay(day, new Date());
              return (
                <div key={i} className={cn("p-2 text-center border-r border-border last:border-r-0", isToday ? "bg-emerald-500/10" : "bg-muted/50")}>
                  <p className="text-xs font-medium text-muted-foreground">{format(day, "EEE", { locale: fr })}</p>
                  <p className={cn("text-base font-bold", isToday && "text-emerald-600")}>{format(day, "d")}</p>
                </div>
              );
            })}
          </div>

          {HOURS.map((hour) => (
            <div key={hour} className="grid grid-cols-6 border-b border-border last:border-b-0">
              <div className="p-2 bg-muted/30 border-r border-border flex items-start justify-center">
                <span className="text-xs text-muted-foreground">{hour}:00</span>
              </div>
              {weekDays.map((day, di) => {
                const slotRdvs = getRdvsForSlot(day, hour);
                const isToday = isSameDay(day, new Date());
                const adminBusy = isSlotBusyByAdmin(day, hour);
                const myRdvs = slotRdvs.filter(r => isMyRdv(r));
                const isFree = slotRdvs.length === 0;

                return (
                  <div key={di}
                    className={cn("p-1 min-h-[60px] border-r border-border last:border-r-0 transition-colors",
                      isToday && "bg-emerald-500/10",
                      adminBusy && !myRdvs.length && "bg-muted/50",
                      isFree && "bg-card"
                    )}
                    data-testid={`slot-${format(day, "yyyy-MM-dd")}-${hour}`}
                  >
                    {/* My RDVs */}
                    {myRdvs.map(rdv => (
                      <div key={rdv.id}
                        className="w-full p-1.5 rounded-lg text-xs mb-1 border bg-emerald-500/10 border-emerald-500/30 cursor-pointer"
                        onClick={() => setSelectedRdv(rdv)}
                        data-testid={`rdv-${rdv.id}`}
                      >
                        <div className="flex items-center gap-1"><Clock className="w-3 h-3 text-[hsl(var(--renova-green))]" /><span className="font-semibold text-emerald-700">{rdv.heure}</span></div>
                        <p className="font-medium truncate">{rdv.client_nom}</p>
                      </div>
                    ))}
                    {/* Admin busy (not mine) */}
                    {adminBusy && myRdvs.length === 0 && (
                      <div className="w-full p-1.5 rounded-lg text-xs border bg-muted/30 border-gray-200">
                        <div className="flex items-center gap-1 text-gray-400">
                          <Clock className="w-3 h-3" /><span className="font-medium">Occupé</span>
                        </div>
                      </div>
                    )}
                    {/* Free slot indicator */}
                    {isFree && (
                      <div className="w-full h-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <span className="text-[10px] text-emerald-500 font-medium">{adminName}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* RDV Detail Dialog */}
      <Dialog open={!!selectedRdv} onOpenChange={() => setSelectedRdv(null)}>
        <DialogContent className="rounded-xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Mon RDV</DialogTitle></DialogHeader>
          {selectedRdv && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm">
                <Calendar className="w-4 h-4 inline mr-2 text-[hsl(var(--renova-green))]" />
                {selectedRdv.date && format(parseISO(selectedRdv.date), "EEEE d MMMM yyyy", { locale: fr })} à {selectedRdv.heure}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><User className="w-4 h-4 text-muted-foreground" /><span className="font-medium">{selectedRdv.client_nom}</span></div>
                {selectedRdv.client_telephone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /><span>{selectedRdv.client_telephone}</span></div>}
                {selectedRdv.client_adresse && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" /><span>{selectedRdv.client_adresse}</span></div>}
                {selectedRdv.programme && <div className="text-xs text-muted-foreground">Programme : <strong>{selectedRdv.programme}</strong></div>}
                {selectedRdv.notes && <div className="p-2 rounded-lg bg-muted text-xs">{selectedRdv.notes}</div>}
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Attribué à :</span>
                <span className="font-semibold">{selectedRdv.attribue_a}</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Statut :</span>
                <span className={cn("px-2 py-0.5 rounded-full font-bold",
                  selectedRdv.statut === "planifie" ? "bg-blue-500/20 text-blue-600 dark:text-blue-400" :
                  selectedRdv.statut === "realise" ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : "bg-red-500/20 text-red-600 dark:text-red-400"
                )}>{selectedRdv.statut === "planifie" ? "Planifié" : selectedRdv.statut === "realise" ? "Réalisé" : "Annulé"}</span>
              </div>
              {selectedRdv.dossier_id && (
                <Button variant="outline" className="w-full rounded-lg mt-2" onClick={() => { setSelectedRdv(null); navigate(`/dossier-regie/${selectedRdv.dossier_id}`); }}>
                  Voir le dossier
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New RDV Modal */}
      <Dialog open={showNewRdv} onOpenChange={setShowNewRdv}>
        <DialogContent className="rounded-xl max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-blue-500" />Créer un RDV — {adminName}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold">Date *</label>
                <Input type="date" value={newRdvForm.date} min={new Date().toISOString().split("T")[0]} onChange={e => setNewRdvForm(p => ({...p, date: e.target.value, heure: ""}))} className="rounded-lg mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold">Créneau *</label>
                {newRdvForm.date ? (
                  <select value={newRdvForm.heure} onChange={e => setNewRdvForm(p => ({...p, heure: e.target.value}))} className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-sm">
                    <option value="">Choisir...</option>
                    {HOURS.map(h => `${String(h).padStart(2,"0")}:00`).filter(h => !busySlotsNew.includes(h)).map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                ) : <div className="mt-1 px-3 py-2 rounded-lg border border-border bg-muted text-sm text-muted-foreground">Sélectionnez une date</div>}
                {busySlotsNew.length > 0 && <p className="text-[10px] text-amber-600 mt-1">{busySlotsNew.length} créneau(x) occupé(s)</p>}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold">Nom *</label>
              <Input value={newRdvForm.client_nom} onChange={e => setNewRdvForm(p => ({...p, client_nom: e.target.value}))} className="rounded-lg mt-1" placeholder="Nom du client" />
            </div>
            <div>
              <label className="text-xs font-semibold">Téléphone</label>
              <Input value={newRdvForm.client_telephone} onChange={e => setNewRdvForm(p => ({...p, client_telephone: e.target.value}))} className="rounded-lg mt-1" placeholder="06..." />
            </div>
            <div>
              <label className="text-xs font-semibold">Objet du RDV</label>
              <Input value={newRdvForm.notes} onChange={e => setNewRdvForm(p => ({...p, notes: e.target.value}))} className="rounded-lg mt-1" placeholder="Objet..." />
            </div>
            <div>
              <label className="text-xs font-semibold">Relier à un dossier</label>
              <Input placeholder="Rechercher..." value={newRdvForm.dossierSearch} onChange={e => setNewRdvForm(p => ({...p, dossierSearch: e.target.value}))} className="rounded-lg mt-1" />
              {newRdvForm.dossierSearch && dossiersList.filter(d => (d.client_name || "").toLowerCase().includes(newRdvForm.dossierSearch.toLowerCase())).slice(0, 5).map(d => (
                <button key={d.id} onClick={() => setNewRdvForm(p => ({...p, dossier_id: d.id, dossierSearch: "", client_nom: p.client_nom || d.client_name}))}
                  className="w-full text-left px-3 py-1 text-xs hover:bg-muted/50 border-b border-border">
                  <span className="font-semibold">{d.client_name}</span> <span className="text-muted-foreground">({d.programme})</span>
                </button>
              ))}
              {newRdvForm.dossier_id && <p className="text-[10px] text-emerald-600 mt-1">Dossier lié</p>}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowNewRdv(false)} className="flex-1 rounded-lg">Annuler</Button>
              <Button onClick={handleCreateRdv} disabled={savingRdv || !newRdvForm.client_nom.trim() || !newRdvForm.date || !newRdvForm.heure} className="flex-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white">
                {savingRdv ? "Création..." : "Créer le RDV"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
